/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '../../../store/hooks';
import { fetchPermissions } from '../../../features/auth/authSlice';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Toast } from 'sharedUi/Toast';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { sortRows } from '../../../utils/client-sort';
import { permissionSetsApi, permissionsApi } from '../../../services/rbac.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import type {
  PermissionSet,
  PermissionSetCreateRequest,
  PermissionSetUpdateRequest,
  RbacStatus,
} from '../../../types/rbac';
import SelectionPanel from '../../../components/rbac/selection-panel';
import DependencyDeleteDialog from '../../../components/rbac/dependency-delete-dialog';
import EntityLinksDialog, { LinkCard, LinkChips } from '../../../components/rbac/entity-links-dialog';
import ProjectCell from '../../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../../components/common/saas-data-table';
import {
  StatusTag,
  DetailShell,
  DetailCard,
  DetailRow,
  Chips,
  FormShell,
  CountBadge,
  useRbacToast,
  Field,
  TextField,
  TextAreaField,
} from './shared';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'displayName', header: 'Display Name', filter: true }];

interface FormState {
  key: string;
  displayName: string;
  description: string;
  status: RbacStatus;
  permissionIds: string[];
  appId: string;
}

const EMPTY: FormState = {
  key: '',
  displayName: '',
  description: '',
  status: 'ACTIVE',
  permissionIds: [],
  appId: '',
};

const PermissionSetsTab = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { toast, showSuccess, showError } = useRbacToast();
  const canCreate = useHasPermission(PERMISSIONS.AUTH_PERMISSION_SET_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.AUTH_PERMISSION_SET_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.AUTH_PERMISSION_SET_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // In-page (client-side) sort of the currently-loaded page. Search + filters stay server-side.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [showGlobal, setShowGlobal] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<PermissionSet | null>(null);
  const [linksRow, setLinksRow] = useState<PermissionSet | null>(null);

  const page = Math.floor(first / pageSize) + 1;
  const filtersParam = buildColumnFiltersParam(filters);
  const appIdParam = selectedProjectId || undefined;

  // Projects the caller may access — drives the project filter + form dropdown.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['rbac', 'permission-sets', debouncedSearch, page, pageSize, filtersParam, appIdParam, showGlobal],
    queryFn: () =>
      permissionSetsApi.list({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
        filters: filtersParam,
        app_id: appIdParam,
        include_global: showGlobal,
      }),
  });

  const handleFilter = (event: any) => {
    setFilters(event.filters || {});
    setFirst(0);
  };

  // All permissions — used only for the key<->id maps (translate any set's keys).
  const { data: allPermissions = [] } = useQuery({
    queryKey: ['rbac', 'permissions', 'all'],
    queryFn: () => permissionsApi.listAll(),
  });

  // Picker options scoped to the selected project (+ global): a set for project X only
  // offers X's permissions and the global ones.
  const pickerPermAppId = form.appId || undefined;
  const { data: pickerPermissions = [] } = useQuery({
    queryKey: ['rbac', 'permissions', 'picker', pickerPermAppId],
    queryFn: () => permissionsApi.listAll(undefined, { appId: pickerPermAppId, includeGlobal: true }),
  });

  // Map permission KEY -> _id (for translating a set's permissionKeys back to ids on edit).
  const keyToId = useMemo(() => {
    const m = new Map<string, string>();
    allPermissions.forEach((p) => m.set(p.key, p._id));
    return m;
  }, [allPermissions]);

  // Reverse map (_id -> KEY) for the resolved-permissions preview in the form.
  const idToKey = useMemo(() => {
    const m = new Map<string, string>();
    allPermissions.forEach((p) => m.set(p._id, p.key));
    return m;
  }, [allPermissions]);

  const selectedPermissionKeys = useMemo(
    () =>
      form.permissionIds
        .map((id) => idToKey.get(id))
        .filter((k): k is string => !!k)
        .sort(),
    [form.permissionIds, idToKey]
  );

  const permOptions = useMemo(
    () => pickerPermissions.map((p) => ({ id: p._id, label: p.displayName || p.key, meta: p.key })),
    [pickerPermissions]
  );

  const rows = data?.data || [];
  const displayRows = useMemo(() => sortRows(rows, sortField, sortOrder), [rows, sortField, sortOrder]);
  const total = data?.pagination?.totalItems ?? (data?.pagination as any)?.total ?? 0;

  const setField = (k: keyof FormState, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // Lock the project on create when the caller can access exactly one project.
  const onlyProjectId = accessibleProjects.length === 1 ? accessibleProjects[0]._id : '';

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY, appId: onlyProjectId });
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row: PermissionSet) => {
    setEditId(row._id);
    // Backend returns permissionKeys; translate to permission _ids for the picker.
    const ids = (row.permissionKeys || [])
      .map((k) => keyToId.get(k))
      .filter((v): v is string => !!v);
    setForm({
      key: row.key,
      displayName: row.displayName || '',
      description: row.description || '',
      status: row.status || 'ACTIVE',
      permissionIds: ids,
      appId: row.appId || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rbac', 'permission-sets'] });
    // A set's permission keys changed → refresh the current user's resolved perms.
    dispatch(fetchPermissions());
  };

  const createMut = useMutation({
    mutationFn: (payload: PermissionSetCreateRequest) => permissionSetsApi.create(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission set created');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to create permission set'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: PermissionSetUpdateRequest) => permissionSetsApi.update(editId!, payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission set updated');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to update permission set'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => permissionSetsApi.remove(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Permission set deleted');
      setDeleteId(null);
    },
    onError: (e) => {
      showError(e, 'Failed to delete permission set');
      setDeleteId(null);
    },
  });

  const saving = createMut.isPending || updateMut.isPending;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.key.trim()) err.key = 'Key is required';
    // Backend requires a display name on create — validate it here for parity.
    if (!form.displayName.trim()) err.displayName = 'Display name is required';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    if (editId) {
      updateMut.mutate({
        displayName: form.displayName || undefined,
        description: form.description || undefined,
        permissionIds: form.permissionIds,
        status: form.status,
      });
    } else {
      createMut.mutate({
        key: form.key.trim(),
        displayName: form.displayName || undefined,
        description: form.description || undefined,
        permissionIds: form.permissionIds,
        status: form.status,
        appId: form.appId || undefined,
      });
    }
  };

  const columns: SaasColumn<PermissionSet>[] = useMemo(
    () => [
      { field: 'key', header: 'Key', sortable: true, filter: true, hideable: false, body: (r) => <span className="font-mono text-xs">{r.key}</span> },
      { field: 'displayName', header: 'Display Name', sortable: true, filter: true, body: (r) => r.displayName || '-' },
      {
        field: 'permissions',
        header: 'Permissions',
        body: (r) => <CountBadge value={(r.permissionKeys || []).length} />,
      },
      {
        field: 'appId',
        header: 'Project',
        body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} />,
      },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-1">
            <button
              className="rounded p-2 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={() => setViewRow(r)}
              aria-label="View"
            >
              <i className="pi pi-eye text-emerald-600" />
            </button>
            <button
              className="rounded p-2 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={() => setLinksRow(r)}
              aria-label="Links"
              title="Linked entities"
            >
              <i className="pi pi-sitemap text-indigo-600" />
            </button>
            {canUpdate && (
              <button
                className="rounded p-2 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => openEdit(r)}
                aria-label="Edit"
              >
                <i className="pi pi-pencil text-blue-600" />
              </button>
            )}
            {canDelete && (
              <button
                className="rounded p-2 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setDeleteId(r._id)}
                aria-label="Delete"
              >
                <i className="pi pi-trash text-red-600" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [keyToId, accessibleProjects]
  );

  // Full-page detail view (replaces the old dialog).
  if (viewRow) {
    const perms = viewRow.permissionKeys || [];
    const projName = accessibleProjects.find((p: AccessibleProject) => p._id === viewRow.appId)?.name || 'Global';
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <DetailShell
          backLabel="Back to Permission Sets"
          onBack={() => setViewRow(null)}
          title={viewRow.displayName || viewRow.key}
          subtitle={`${viewRow.key} · ${projName}`}
          actions={canUpdate ? <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => { const r = viewRow; setViewRow(null); openEdit(r); }} /> : undefined}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <DetailCard title="Permission Set">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <DetailRow label="Key" value={viewRow.key} mono />
                <DetailRow label="Status" value={<StatusTag status={viewRow.status} />} />
                <DetailRow label="Display name" value={viewRow.displayName || '—'} />
                <DetailRow label="Project" value={projName} />
                {viewRow.description && (
                  <div className="col-span-2"><DetailRow label="Description" value={viewRow.description} /></div>
                )}
              </div>
            </DetailCard>
            <DetailCard title="Audit">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {(viewRow as any).createdAt && <DetailRow label="Created" value={formatTimestamp((viewRow as any).createdAt)} />}
                {(viewRow as any).updatedAt && <DetailRow label="Updated" value={formatTimestamp((viewRow as any).updatedAt)} />}
                <div className="col-span-2"><DetailRow label="Set ID" value={viewRow._id} mono /></div>
              </div>
            </DetailCard>
          </div>
          <div className="mt-3">
            <DetailCard title="Permissions" count={perms.length}>
              <div className="max-h-72 overflow-auto"><Chips items={perms} tone="slate" /></div>
            </DetailCard>
          </div>
        </DetailShell>
      </>
    );
  }

  // Full-page create / edit form (replaces the old dialog).
  if (dialogOpen) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <FormShell
          backLabel="Back to Permission Sets"
          onBack={() => !saving && setDialogOpen(false)}
          title={editId ? 'Edit Permission Set' : 'Create Permission Set'}
          onCancel={() => setDialogOpen(false)}
          onSave={save}
          saving={saving}
          isEdit={!!editId}
        >
          <div className="flex flex-col gap-3">
            {/* Project first — it scopes which permissions can be picked below. */}
            <Field label="Project">
              <Dropdown
                value={form.appId || null}
                options={[
                  { label: 'Global (all projects)', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e) => setForm((p) => ({ ...p, appId: e.value ?? '', permissionIds: [] }))}
                placeholder="Select project"
                disabled={!!editId || accessibleProjects.length <= 1}
                className="w-full md:w-1/2"
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Key" required error={errors.key}>
                <TextField value={form.key} onChange={(v) => setField('key', v)} placeholder="e.g. personnel.manage" disabled={!!editId} />
              </Field>
              <Field label="Display Name" required error={errors.displayName}>
                <TextField value={form.displayName} onChange={(v) => setField('displayName', v)} placeholder="Enter display name" />
              </Field>
            </div>

            <Field label="Description">
              <TextAreaField value={form.description} onChange={(v) => setField('description', v)} placeholder="Optional description" rows={2} />
            </Field>

            <SelectionPanel
              title="Permissions"
              helper={`${form.permissionIds.length} selected`}
              options={permOptions}
              selectedIds={form.permissionIds}
              onChange={(ids) => setField('permissionIds', ids)}
            />

            {/* Resolved permissions preview (read-only) — the keys this set will grant. */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Resolved permissions ({selectedPermissionKeys.length})
              </label>
              <div className="max-h-32 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900/40">
                {selectedPermissionKeys.length === 0 ? (
                  <span className="text-xs text-ink-muted">
                    Select permissions above to preview the keys this set grants.
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedPermissionKeys.map((k) => (
                      <span key={k} className="rounded bg-gray-200 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </FormShell>
      </>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <SaasDataTable
        tableId="rbac-permission-sets"
        columns={columns}
        data={displayRows}
        loading={isFetching}
        dataKey="_id"
        emptyMessage="No permission sets found"
        first={first}
        rows={pageSize}
        totalRecords={total}
        onPage={(e: any) => {
          setFirst(e.first);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={(e: any) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        filters={filters}
        onFilter={handleFilter}
        globalSearch={search}
        onGlobalSearchChange={(v) => {
          setSearch(v);
          setFirst(0);
        }}
        globalSearchPlaceholder="Search permission sets..."
        testId="PermissionSetsList"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-3">
            {accessibleProjects.length > 1 && (
              <Dropdown
                value={selectedProjectId}
                options={[
                  { label: 'All projects', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e: any) => {
                  setSelectedProjectId(e.value ?? null);
                  setFirst(0);
                }}
                placeholder="All projects"
                style={{ minWidth: '190px' }}
              />
            )}
            {/* Grouped with the toggle in one pill so the two filters read as a
                pair rather than two loose controls floating by the search box. */}
            <label className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <InputSwitch checked={showGlobal} onChange={(e: any) => { setShowGlobal(!!e.value); setFirst(0); }} />
              Show core
            </label>
          </div>
        }
        toolbarEnd={
          <>
            <span className="whitespace-nowrap text-sm text-ink-muted">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
            <RefreshButton
              onClick={() => queryClient.invalidateQueries({ queryKey: ['rbac', 'permission-sets'] })}
              loading={isFetching}
            />
            {canCreate && <Button label="Create Permission Set" icon="pi pi-plus" onClick={openCreate} size="small" />}
          </>
        }
      />


      <DependencyDeleteDialog
        entityId={deleteId}
        entityLabel="permission set"
        resourceKey="permission-set"
        getDependencies={permissionSetsApi.getDependencies}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        deleting={deleteMut.isPending}
      />

      <EntityLinksDialog
        entityId={linksRow?._id ?? null}
        title="Permission Set Links"
        resourceKey="permission-set"
        getDependencies={permissionSetsApi.getDependencies}
        onHide={() => setLinksRow(null)}
      >
        <LinkCard icon="pi pi-key" title="Contains Permissions" count={(linksRow?.permissionKeys || []).length}>
          <LinkChips items={linksRow?.permissionKeys || []} tone="emerald" />
        </LinkCard>
      </EntityLinksDialog>

    </div>
  );
};

export default PermissionSetsTab;
